import React from "react"
import styled from "styled-components"
import { humanReadableSize } from "../../../../Computation/utilFunctions"


// Minimal storage indicator for the Export Tracker modal / per-patient tab
// headers. A thin horizontal bar + compact "used / total" text — sized to sit
// quietly in a header row alongside the tabs, not a feature widget. Renders:
//   - loading       (stats === null)        → shimmer bar skeleton
//   - no-total      (disk_total_bytes null) → "X used" text, no bar
//   - normal/full   → label · bar · "used / total" (+ red note when full)
//
// Bar color tiers match the status-pill palette:
//   < 75% blue · 75–90% orange · ≥ 90% red.


const Row = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--text-secondary-color);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
`

const Label = styled.span`
    font-weight: 600;
    color: var(--text-secondary-color);
`

const Track = styled.span`
    display: inline-block;
    width: 96px;
    height: 6px;
    background: var(--color-hover-neutral);
    border-radius: 999px;
    overflow: hidden;
`

const Fill = styled.span.attrs(props => ({
    style: {
        width: `${Math.min(Math.max(props.$pct, 2), 100)}%`,
        background: props.$color,
    },
}))`
    display: block;
    height: 100%;
    border-radius: 999px;
    transition: width 0.5s ease, background 0.25s ease;
`

const Values = styled.span`
    color: var(--text-primary-color);
    font-weight: 600;
`

const Muted = styled.span`
    color: var(--text-secondary-color);
    font-weight: 400;
`

const FullNote = styled.span`
    color: #F44336;
    font-weight: 600;
`

const Skeleton = styled.span`
    display: inline-block;
    width: 180px;
    height: 6px;
    border-radius: 999px;
    background: linear-gradient(
        90deg,
        rgba(33, 150, 243, 0.08) 0%,
        rgba(33, 150, 243, 0.22) 50%,
        rgba(33, 150, 243, 0.08) 100%
    );
    background-size: 200% 100%;
    animation: storage-bar-shimmer 1.4s ease-in-out infinite;

    @keyframes storage-bar-shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
`

function fillColor(pct) {
    if (pct >= 90) return "#F44336"
    if (pct >= 75) return "#FF9800"
    return "var(--color-accent-interactive)"
}


export default function ExportsStorageMeter({ stats }) {
    if (!stats) {
        return <Row><Skeleton aria-label="Loading export storage" /></Row>
    }

    const { total_bytes, disk_total_bytes, quota_source } = stats

    // No known total → can't draw a bar. Compact text fallback.
    if (!disk_total_bytes) {
        return <Row><Values>{humanReadableSize(total_bytes)}</Values><Muted>used</Muted></Row>
    }

    const pct = Math.min(100, (total_bytes / disk_total_bytes) * 100)
    const color = fillColor(pct)
    // Derive "full" from the same numbers the bar uses so the label and the bar
    // can never disagree. (disk_free_bytes is clamped to physical free space, so
    // using it here could read "full" while the quota bar is well under 100%.)
    const isFull = total_bytes >= disk_total_bytes

    const tooltip = quota_source === "quota"
        ? `Configured export storage limit for this deployment. ${pct.toFixed(1)}% used.`
        : `Total size of the underlying filesystem, shared with other patient data. ${pct.toFixed(1)}% used.`

    return (
        <Row title={tooltip}>
            <Label>Storage</Label>
            <Track>
                <Fill $pct={pct} $color={color} />
            </Track>
            <span>
                <Values>{humanReadableSize(total_bytes)}</Values>
                <Muted> / {humanReadableSize(disk_total_bytes)}</Muted>
            </span>
            {isFull && <FullNote>Full. Delete exports to free space.</FullNote>}
        </Row>
    )
}
